/**
 * Which properties this dashboard's tab strip should offer (DOP-479).
 *
 * `table.properties` is the account's whole property roster — the backend
 * builds it from an UNSCOPED `list_property_keys`, so on a workflow-scoped grid
 * (DOP-476) it happily names properties this workflow extracted nothing for.
 * Opening one of those tabs is a dead end: a grid with rows and no numbers.
 *
 * The portfolio rollup is what can answer the question, because its cells carry
 * `contributors` — one entry per property that put a number into the cell. So
 * the roster is narrowed to the properties that actually contributed, and a
 * grid that cannot answer (a single-property table, or a rollup whose server
 * predates contributors) narrows nothing rather than guessing.
 */

import type { KpiTable } from "../../types/kpi";

/**
 * Properties with at least one value on this rollup grid, or `null` when the
 * table cannot say — the caller must then show the roster unnarrowed.
 */
export function contributingProperties(
	table: KpiTable | null,
): Set<string> | null {
	if (!table || table.rollup !== "portfolio") return null;
	const contributing = new Set<string>();
	let sawContributors = false;
	for (const group of table.groups) {
		for (const metric of group.metrics) {
			for (const cell of Object.values(metric.cells)) {
				if (!cell.contributors) continue;
				sawContributors = true;
				for (const contributor of cell.contributors) {
					if (contributor.value !== null) {
						contributing.add(contributor.property_key);
					}
				}
			}
		}
	}
	// No cell declared contributors at all: a pre-v1.4 server, not an empty
	// portfolio. Hiding every tab on that evidence would be the worse error.
	return sawContributors ? contributing : null;
}
